import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { useRealtimeChannel, userChannel } from '@/core/realtime';
import { useMyMemberPublicId } from '@/stores/session';
import { announcementKeys } from './queries';
import type { AnnouncementPublication } from './types';

/**
 * Refresh the announcement feed when the server publishes a new post.
 *
 * The nudge rides the caller's own `user:` channel (backend ADR-021) — the same
 * channel the notification bell listens on — because entitlement is per member:
 * a class announcement reaches only the members of that class. Registering a
 * SECOND handler on that channel (rather than teaching `useUserChannel` about
 * announcements) keeps the two features' caches owned by their own features;
 * `useRealtimeChannel` fans one subscription out to every handler.
 *
 * The payload carries only an id and a title, so the feed is invalidated rather
 * than patched: what this reader is entitled to see is a server decision.
 */
export function useAnnouncementChannel(): void {
  const qc = useQueryClient();
  const memberPublicId = useMyMemberPublicId();

  const onPublication = useCallback(
    (payload: AnnouncementPublication) => {
      if (payload?.type !== 'announcement.created') return;
      void qc.invalidateQueries({ queryKey: announcementKeys.all });
    },
    [qc]
  );

  useRealtimeChannel<AnnouncementPublication>(
    memberPublicId ? userChannel(memberPublicId) : null,
    onPublication
  );
}
